import React, {useEffect, useState} from 'react';
import {useTheme} from "../../../../common/ProjectThemeProvider";
import {useToast} from "../../../../common/fusion-ui/provider/ToastProvider";
import {
    checkCurrUrlHasParam,
    checkNullArr,
    checkNullJson,
    checkNullStr,
    getArrLen,
    getJsonValue,
    getEncodedUrl,
    isBoolTrue,
    isValueBool,
    subtractSize,
    trim
} from "../../../../common/fusion-ui/utils/ReactUtils";
import {
    getAllocatedModuleName,
    getSearchQueryFromUrl,
    getValidSearchTargetIdFromUrl,
    getValidSearchTargetIdNameFromUrl,
    getValidSearchTypeIdFromUrl,
    getValidSearchTypeIdNameFromUrl,
    isModuleAllocated,
    isUrlSearchTargetIdValid,
    isUrlSearchTypeIdValid,
    printError,
    printLog
} from "../../../utils/AppUtils";
import {Flexbox, Label} from "../../../../common/fusion-ui";
import {ThemedDropdownMenu, ThemedInput} from "../../../index";
import {DashSearchModules, DashSearchTargets, Keys, ModuleRouteUrls, Modules} from "../../../config/AppConfig";
import {useReduxPersistSessionData} from "../../../hook/useReduxPersistSessionData";
import useScreenSize from "../../../../common/fusion-ui/hook/useScreenSize";
import {AnimationType, FramerAnimator} from "../../../../common/fusion-ui/animation/FramerAnimator";

const DashSearchView = React.memo(({
                                       className = "", width = "300px", style = {}, onSearchChange, onSearchClick, ...rest
                                   }) => {
    const TAG = "DashSearchView";

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isScreenUpSm} = useScreenSize();
    const animator = new FramerAnimator();

    const VIEW_HEIGHT = '32px';
    const INPUT_CHAR_LIMIT = 150;

    const SEARCH_TYPE_DROPDOWN_ITEMS = Object.values(DashSearchModules).map(item => isModuleAllocated(item.name) && ({
        name: getAllocatedModuleName(item.name), id: item.id
    })).filter(item => checkNullJson(item) && !isValueBool(item));
    const DEF_TYPE_OPTION_NAME = checkNullArr(SEARCH_TYPE_DROPDOWN_ITEMS) ? SEARCH_TYPE_DROPDOWN_ITEMS[0].name : '';
    const DEF_TYPE_OPTION_ID = checkNullArr(SEARCH_TYPE_DROPDOWN_ITEMS) ? SEARCH_TYPE_DROPDOWN_ITEMS[0].id : '';
    // const DEF_TYPE_OPTION_NAME = DashSearchModules.res.name;
    // const DEF_TYPE_OPTION_ID = DashSearchModules.res.id;

    const SEARCH_TARGET_DROPDOWN_ITEMS = Object.values(DashSearchTargets).map(item => ({name: item.name, id: item.id}));
    const DEF_TARGET_OPTION_NAME = DashSearchTargets.titleDesc.name;
    const DEF_TARGET_OPTION_ID = DashSearchTargets.titleDesc.id;

    const SEARCH_INPUT_ID = `${TAG}SEARCH_INPUT_BOX`;

    const Fields = {
        searchBarInputValue: 'searchBarInputValue',
        searchBarInputValueOld: 'searchBarInputValueOld',
        typeDropdownCurrOptionName: Keys.store_Dash_HomeTab_searchInputDropdownName,
        typeDropdownCurrOptionId: Keys.store_Dash_HomeTab_searchInputDropdownId,
        targetDropdownCurrOptionName: Keys.store_Dash_HomeTab_searchTargetName,
        targetDropdownCurrOptionId: Keys.store_Dash_HomeTab_searchTargetId,
        searchedList: 'searchedList',
        disabledTypeDropdown: 'disabledTypeDropdown',
        disabledTargetDropdown: 'disabledTargetDropdown',
        isTypeDropdownOpened: 'isTypeDropdownOpened',
    };
    const [fieldsData, setFieldsData] = useState({});

    const {updateSessionStoreFieldValue, getSessionStoreFieldValue} = useReduxPersistSessionData({
        keys: [Fields.typeDropdownCurrOptionName, Fields.typeDropdownCurrOptionId, Fields.targetDropdownCurrOptionName, Fields.targetDropdownCurrOptionId,],
    });

    useEffect(() => {
        if (!checkNullStr(getField(Fields.typeDropdownCurrOptionName))) {
            updateField(Fields.typeDropdownCurrOptionName, DEF_TYPE_OPTION_NAME);
            updateField(Fields.typeDropdownCurrOptionId, DEF_TYPE_OPTION_ID);
            updateField(Fields.targetDropdownCurrOptionName, DEF_TARGET_OPTION_NAME);
            updateField(Fields.targetDropdownCurrOptionId, DEF_TARGET_OPTION_ID);

            updateSessionFieldValue(Fields.typeDropdownCurrOptionName, DEF_TYPE_OPTION_NAME);
            updateSessionFieldValue(Fields.typeDropdownCurrOptionId, DEF_TYPE_OPTION_ID);
            updateSessionFieldValue(Fields.targetDropdownCurrOptionName, DEF_TARGET_OPTION_NAME);
            updateSessionFieldValue(Fields.targetDropdownCurrOptionId, DEF_TARGET_OPTION_ID);
        }

        if (isSearchPage()) {
            const searchQueryParamFound = checkCurrUrlHasParam(Keys.urlParam_SearchQuery);
            if (searchQueryParamFound) {
                //get search-query & search-type from url
                const isSearchTypeIdValid = isUrlSearchTypeIdValid();
                const searchQuery = getSearchQueryFromUrl();
                updateField(Fields.searchBarInputValue, searchQuery);
                updateField(Fields.searchBarInputValueOld, searchQuery);
                if (isSearchTypeIdValid) {
                    const searchTypeId = getValidSearchTypeIdFromUrl();
                    const searchTypeName = getValidSearchTypeIdNameFromUrl();
                    updateField(Fields.typeDropdownCurrOptionName, searchTypeName);
                    updateField(Fields.typeDropdownCurrOptionId, searchTypeId);
                    updateSessionFieldValue(Fields.typeDropdownCurrOptionName, searchTypeName);
                    updateSessionFieldValue(Fields.typeDropdownCurrOptionId, searchTypeId);
                    updateField(Fields.disabledTypeDropdown, false);
                    // log('useEffect.searchQuery:', searchQuery, 'searchTypeName:', searchTypeName, 'searchTypeId:', searchTypeId, 'isSearchTypeIdValid:', isSearchTypeIdValid);
                } else {
                    //update with default values
                    updateField(Fields.typeDropdownCurrOptionName, DEF_TYPE_OPTION_NAME);
                    updateField(Fields.typeDropdownCurrOptionId, DEF_TYPE_OPTION_ID);
                    updateSessionFieldValue(Fields.typeDropdownCurrOptionName, DEF_TYPE_OPTION_NAME);
                    updateSessionFieldValue(Fields.typeDropdownCurrOptionId, DEF_TYPE_OPTION_ID);
                    updateField(Fields.disabledTypeDropdown, true);
                }


                //extract search-target from url
                const isSearchTargetIdValid = isUrlSearchTargetIdValid();
                if (isSearchTargetIdValid) {
                    const searchTargetId = getValidSearchTargetIdFromUrl();
                    const searchTargetName = getValidSearchTargetIdNameFromUrl();
                    updateField(Fields.targetDropdownCurrOptionName, searchTargetName);
                    updateField(Fields.targetDropdownCurrOptionId, searchTargetId);
                    updateSessionFieldValue(Fields.targetDropdownCurrOptionName, searchTargetName);
                    updateSessionFieldValue(Fields.targetDropdownCurrOptionId, searchTargetId);
                    updateField(Fields.disabledTargetDropdown, false);
                    // log('useEffect.searchTargetName:', searchTargetName, 'searchTargetId:', searchTargetId, 'isSearchTargetIdValid:', isSearchTargetIdValid);
                } else {
                    //update with default values
                    updateField(Fields.targetDropdownCurrOptionName, DEF_TARGET_OPTION_NAME);
                    updateField(Fields.targetDropdownCurrOptionId, DEF_TARGET_OPTION_ID);
                    updateSessionFieldValue(Fields.targetDropdownCurrOptionName, DEF_TARGET_OPTION_NAME);
                    updateSessionFieldValue(Fields.targetDropdownCurrOptionId, DEF_TARGET_OPTION_ID);
                    updateField(Fields.disabledTargetDropdown, true);
                }
            }
        }
    }, []);

    return (<>
        {animator.setAnimationType(AnimationType.SLIDE_DOWN).setDuration(700).render(<div className={`m-0 p-0 ${className}`}
                                                                                          style={style} {...rest}>
            <Flexbox className={'m-0 p-0 rounded-3'} position={'relative'} overflow={'hidden'}
                     justifyAt={'start'} alignAt={'center'} borderVariant={1}
                     style={{borderColor: theme.primary, width: (isScreenUpSm) ? width : 'auto'}}
            >
                {/*input*/}
                <ThemedInput
                    mainWrapperClassName={'w-100'}
                    wrapperClassName={'m-0 p-0 w-100'}
                    className={`border-none me-3 ${checkNullStr(getSessionFieldValue(Fields.searchInputValue)) ? 'pe-5' : ''}`}
                    id={SEARCH_INPUT_ID}
                    showMsg={false}
                    showLabel={false}
                    style={{height: VIEW_HEIGHT}}
                    rightIcon={`fa fa-search fs-normal text-danger cursor-pointer circular highlight-theme-full-hover transition-sm d-flex al-center jc-center text-white-hover ${canDisableSearchBtn() ? 'disable-item cursor-default' : 'text-theme-dark'}`}
                    iconStyle={{
                        marginRight: '-.3rem',
                        width: subtractSize(VIEW_HEIGHT, 6),
                        height: subtractSize(VIEW_HEIGHT, 6),
                        color: !canDisableSearchBtn() ? theme.primary : theme.text50,
                        backgroundColor: !canDisableSearchBtn() ? theme.primary20 : theme.primary05,
                    }}
                    onRightIconClick={handleSearchClick}
                    placeholder={`Search here...`}
                    value={getField(Fields.searchBarInputValue)}
                    onChange={handleSearchChange}
                    maxLength={INPUT_CHAR_LIMIT}
                    onKeyUp={(e) => {
                        if (e.key == 'Enter' && !canDisableSearchBtn()) {
                            // log('search.onKeyUp.Enter.inputText:', getField(Fields.searchBarInputValue));
                            handleSearchClick();
                        }
                    }}
                    // end - loader & search-by dropdown
                    rightComponent={<Flexbox className={''} justifyAt={'end'} alignAt={'center'} position={'absolute'}
                                             style={{
                                                 top: '.38rem', right: '2rem',
                                             }}>

                        {/*search-result count*/}
                        {(checkNullStr(getSessionFieldValue(Fields.searchInputValue)) && checkNullArr(getField(Fields.searchedList))) && <Label
                            className={`m-0 p-0 px-2 me-1 text-nowrap fs-sm rounded-10 select-none fw-semi-bold d-flex al-center flex-nowrap jc-center`}
                            tooltip={'Search Results'}
                            color={theme.text}
                            bgColor={theme.text20}
                            height={'20px'}>
                            {getArrLen(getField(Fields.searchedList))}
                        </Label>}
                        {/*shortcut*/}
                        {/*{(!canDisableSearchBtn()) && <Label*/}
                        {/*    className={`m-0 p-0 px-2 mx-1 text-nowrap fs-sm rounded-2 select-none fw-semi-bold d-flex al-center flex-nowrap jc-center`}*/}
                        {/*    color={theme.text90}*/}
                        {/*    bgColor={theme.text10}*/}
                        {/*    height={'20px'}>*/}
                        {/*    {`CTRL+${InputFocusShortcutKey}`}*/}
                        {/*</Label>}*/}
                    </Flexbox>}
                />


                {/*target-dropdown*/}
                {/*<Tooltip title={'Search with'}>*/}
                <ThemedDropdownMenu
                    className={'ms-n2'}
                    buttonClassName={'border-none fw-semi-bold'}
                    buttonHeight={VIEW_HEIGHT}
                    roundedVariant={0}
                    outlined={false}
                    // textPrefix={<span className={'fw-bold'}>Search with:</span>}
                    textPrefix={<span className={'fw-bold'}><i className={'fa fa-bullseye fs-normal'}/></span>}
                    showSelectedOption={false}
                    // disabled={isFieldTrue(Fields.disabledTargetDropdown)}
                    variant={'primaryBlue'}
                    menuItemNameKey={'name'}
                    menuItemIdKey={'id'}
                    tooltip={'Search With'}
                    defOption={getSessionFieldValue(Fields.targetDropdownCurrOptionName, DEF_TARGET_OPTION_NAME)}
                    onExpand={(opened) => {
                        updateField(Fields.isTypeDropdownOpened, isBoolTrue(opened));
                    }}
                    onItemClick={(name, index, item) => {
                        updateSessionFieldValue(Fields.targetDropdownCurrOptionName, name);
                        updateSessionFieldValue(Fields.targetDropdownCurrOptionId, getJsonValue(item, 'id'));
                        updateField(Fields.targetDropdownCurrOptionName, name);
                        updateField(Fields.targetDropdownCurrOptionId, getJsonValue(item, 'id'));
                    }}>{Object.values(SEARCH_TARGET_DROPDOWN_ITEMS)}
                </ThemedDropdownMenu>
                {/*type-dropdown*/}
                <ThemedDropdownMenu
                    className={''}
                    buttonClassName={'border-none fw-semi-bold'}
                    buttonHeight={VIEW_HEIGHT}
                    roundedVariant={0}
                    outlined={false}
                    // disabled={isFieldTrue(Fields.disabledTypeDropdown)}
                    menuItemNameKey={'name'}
                    menuItemIdKey={'id'}
                    defOption={getSessionFieldValue(Fields.typeDropdownCurrOptionName, DEF_TYPE_OPTION_NAME)}
                    onItemClick={(name, index, item) => {
                        updateSessionFieldValue(Fields.typeDropdownCurrOptionName, name);
                        updateSessionFieldValue(Fields.typeDropdownCurrOptionId, getJsonValue(item, 'id'));
                        updateField(Fields.typeDropdownCurrOptionName, name);
                        updateField(Fields.typeDropdownCurrOptionId, getJsonValue(item, 'id'));
                    }}>{Object.values(SEARCH_TYPE_DROPDOWN_ITEMS)}
                </ThemedDropdownMenu>

            </Flexbox>
        </div>)}
    </>)

    function handleSearchChange(e) {
        const value = e.target.value;
        updateField(Fields.searchBarInputValue, value);
        // if (onSearchChange) onSearchChange(e, value);
        // log('SearchInput.onChange.value:', value);
    }

    function handleSearchClick() {
        const fun = 'handleSearchClick';
        try {
            const searchValue = trim(getField(Fields.searchBarInputValue));
            const params = {
                [Keys.urlParam_SearchQuery]: searchValue,
                [Keys.urlParam_SearchTypeId]: getField(Fields.typeDropdownCurrOptionId),
                [Keys.urlParam_SearchTargetId]: getField(Fields.targetDropdownCurrOptionId),
            }
            const searchUrl = getEncodedUrl(ModuleRouteUrls[Modules.Search], params);
            // log(fun, 'params:', params, '\nsearchUrl:', searchUrl);
            if (onSearchClick) onSearchClick(searchUrl, searchValue, params);
            window.location.href = searchUrl;
        } catch (error) {
            logErr(fun, error);
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }


    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function updateSessionFieldValue(field, value) {
        updateSessionStoreFieldValue(field, value);
    }

    function getSessionFieldValue(field, defValue) {
        return getSessionStoreFieldValue(field, defValue);
    }

    function canDisableSearchBtn() {
        return !checkNullStr(getField(Fields.searchBarInputValue));
        // return !checkNullStr(getField(Fields.searchBarInputValue)) || (getField(Fields.searchBarInputValueOld) == getField(Fields.searchBarInputValue));
    }

    function isSearchPage() {
        return (window.location.href + '').includes(ModuleRouteUrls[Modules.Search]);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default DashSearchView;
